"use client";

import { useSyncExternalStore } from "react";
import { bookmarkKey, MAX_BOOKMARKS, parseKeys, type Bookmark } from "@/lib/bookmarks-shared";

const STORAGE_KEY = "gcg-merkliste";
const EVENT = "gcg-merkliste";
const EMPTY: Bookmark[] = [];

let cacheRaw: string | null = null;
let cache: Bookmark[] = EMPTY;

function parse(raw: string | null): Bookmark[] {
  if (!raw) return EMPTY;
  try {
    const data = JSON.parse(raw) as { items?: unknown };
    if (!Array.isArray(data.items)) return EMPTY;
    const titles = new Map<string, string>();
    for (const it of data.items as Partial<Bookmark>[]) if (it?.k && it.s) titles.set(`${it.k}:${it.s}`, typeof it.t === "string" ? it.t.slice(0, 200) : "");
    return parseKeys([...titles.keys()]).map((b) => ({ ...b, t: titles.get(bookmarkKey(b)) ?? "" }));
  } catch {
    return EMPTY;
  }
}

function read(): Bookmark[] {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    // Speicher gesperrt (z. B. strenge Datenschutzeinstellungen): Merkliste bleibt leer.
  }
  if (raw !== cacheRaw) {
    cacheRaw = raw;
    cache = parse(raw);
  }
  return cache;
}

function write(items: Bookmark[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, items: items.slice(0, MAX_BOOKMARKS) }));
  } catch {
    return false;
  }
  window.dispatchEvent(new Event(EVENT));
  return true;
}

function subscribe(cb: () => void) {
  const onStorage = (e: StorageEvent) => e.key === STORAGE_KEY && cb();
  window.addEventListener("storage", onStorage);
  window.addEventListener(EVENT, cb);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(EVENT, cb);
  };
}

/** Gemerkte Einträge; null auf dem Server und vor der Hydration (Inhalt noch unbekannt). */
export function useBookmarks(): Bookmark[] | null {
  return useSyncExternalStore(subscribe, read, () => null);
}

export const isFull = () => read().length >= MAX_BOOKMARKS;

export function toggleBookmark(b: Bookmark) {
  const list = read();
  const key = bookmarkKey(b);
  if (list.some((x) => bookmarkKey(x) === key)) return write(list.filter((x) => bookmarkKey(x) !== key));
  if (list.length >= MAX_BOOKMARKS) return false;
  return write([b, ...list]);
}

export const removeBookmark = (key: string) => write(read().filter((x) => bookmarkKey(x) !== key));
export const clearBookmarks = () => write([]);

/** Geteilte Einträge übernehmen; vorhandene bleiben vorne. */
export function mergeBookmarks(items: Bookmark[]) {
  const list = read();
  const have = new Set(list.map(bookmarkKey));
  return write([...list, ...items.filter((b) => !have.has(bookmarkKey(b)))]);
}

/** Umbenannte Einträge auf die neue Adresse umstellen und den Titel auffrischen. */
export function updateBookmarks(moved: Record<string, string>, titles: Map<string, string>) {
  const list = read();
  let changed = false;
  const next = list.map((b) => {
    const to = moved[bookmarkKey(b)];
    const nb = to ? { ...b, s: to.slice(to.indexOf(":") + 1) } : b;
    const t = titles.get(bookmarkKey(nb));
    if (to || (t && t !== b.t)) changed = true;
    return t ? { ...nb, t } : nb;
  });
  const seen = new Set<string>();
  if (changed) write(next.filter((b) => !seen.has(bookmarkKey(b)) && !!seen.add(bookmarkKey(b))));
}
